import Api from 'services/api'

const API_USER_FORGET_PASSWORD = '/auth/forgetPassword'
const API_USER_CHECK_PASSPORT = '/auth/checkPassport/:selector/:token'
const API_USER_RESET_PASSWORD = '/auth/resetPassword/:selector/:token'
const API_USER_REGISTER = '/auth/register'
const API_AUTH_SEND_2FA_CODE = '/auth/send2faCode'
const API_AUTH_VERIFY_2FA_CODE = '/auth/verify2faCode'

const API_AUTH_PASSKEY_REGISTER_OPTIONS = '/auth/passkey/register/options'
const API_AUTH_PASSKEY_REGISTER_VERIFY = '/auth/passkey/register/verify'
const API_AUTH_PASSKEY_LIST = '/auth/passkey'
const API_AUTH_PASSKEY_DELETE = '/auth/passkey/:id'
const API_AUTH_PASSKEY_LOGIN_OPTIONS = '/auth/passkey/login/options'
const API_AUTH_PASSKEY_LOGIN_VERIFY = '/auth/passkey/login/verify'

const API_TEMPORARY_AUTH_SIGNIN = '/temporaryAuth/login'
const API_TEMPORARY_AUTH_RESET_PWD = '/temporaryAuth/resetPwd'
const API_TEMPORARY_AUTH_FORGOT_PWD = '/temporaryAuth/forgotPwd'
const API_TEMPORARY_AUTH_EXISTS_EMAIL = '/temporaryAuth/exists'
const API_TEMPORARY_AUTH_REGISTER = '/temporaryAuth/register'
const API_TEMPORARY_AUTH_SEND_2FA_CODE = '/temporaryAuth/send2faCode'
const API_TEMPORARY_AUTH_VERIFY_2FA_CODE = '/temporaryAuth/verify2faCode'
const API_TEMPORARY_PASSKEY_REGISTER_OPTIONS = '/temporaryAuth/passkey/register/options'
const API_TEMPORARY_PASSKEY_REGISTER_VERIFY = '/temporaryAuth/passkey/register/verify'
const API_TEMPORARY_PASSKEY_LIST = '/temporaryAuth/passkey'
const API_TEMPORARY_PASSKEY_DELETE = '/temporaryAuth/passkey/:id'
const API_TEMPORARY_PASSKEY_LOGIN_OPTIONS = '/temporaryAuth/passkey/login/options'
const API_TEMPORARY_PASSKEY_LOGIN_VERIFY = '/temporaryAuth/passkey/login/verify'

const API_INVESTOR_AUTH_SIGNIN = '/investorAuth/login'
const API_INVESTOR_AUTH_RESET_PWD = '/investorAuth/resetPwd'
const API_INVESTOR_AUTH_FORGOT_PWD = '/investorAuth/forgotPwd'

const API_PARTICIPANT_AUTH_SIGNIN = '/participantAuth/login'
const API_PARTICIPANT_AUTH_RESET_PWD = '/participantAuth/resetPwd'
const API_PARTICIPANT_AUTH_FORGOT_PWD = '/participantAuth/forgotPwd'

export const send2faCode = (data: Record<string, string>) => {
  return Api.post(API_AUTH_SEND_2FA_CODE, data)
}

export const verify2faCode = (data: Record<string, string>) => {
  return Api.post(API_AUTH_VERIFY_2FA_CODE, data)
}

const API_AUTH_IMPERSONATE_SEARCH = '/auth/impersonateSearch'
const API_AUTH_IMPERSONATE = '/auth/impersonate'
const API_AUTH_STOP_IMPERSONATE = '/auth/stopImpersonate'

export const impersonateSearch = (search: string) => {
  return Api.post(API_AUTH_IMPERSONATE_SEARCH, { search })
}

export const impersonateUser = (email: string, isBorrower = false) => {
  return Api.post(API_AUTH_IMPERSONATE, { email, isBorrower })
}

export const stopImpersonate = () => {
  return Api.post(API_AUTH_STOP_IMPERSONATE, {})
}

// ---- Passkey (WebAuthn) MFA ------------------------------------------------

// Enrollment (requires an authenticated session).
export const getPasskeyRegisterOptions = () => {
  return Api.post(API_AUTH_PASSKEY_REGISTER_OPTIONS, {})
}

export const verifyPasskeyRegistration = (data: { response: any; name?: string }) => {
  return Api.post(API_AUTH_PASSKEY_REGISTER_VERIFY, data)
}

export const listPasskeys = () => {
  return Api.get(API_AUTH_PASSKEY_LIST)
}

export const deletePasskey = (id: number) => {
  return Api.delete(API_AUTH_PASSKEY_DELETE, {}, { id })
}

// Login second factor.
export const getPasskeyLoginOptions = (email: string) => {
  return Api.post(API_AUTH_PASSKEY_LOGIN_OPTIONS, { email })
}

export const verifyPasskeyLogin = (data: { email: string; password: string; response: any }) => {
  return Api.post(API_AUTH_PASSKEY_LOGIN_VERIFY, data)
}

export const checkPassport = (selector: string, token: string) => {
  return Api.get(API_USER_CHECK_PASSPORT, {}, { selector, token })
}

export const resetPassword = (selector: string, token: string, password: string) => {
  return Api.post(API_USER_RESET_PASSWORD, { password }, { selector, token })
}

export const sendForgetPasswordEmail = (email: string) => {
  return Api.post(API_USER_FORGET_PASSWORD, { email })
}

export const registerUser = (data: Record<string, any>) => {
  return Api.put(API_USER_REGISTER, data)
}

export const signInTemporaryUser = (data: Record<string, any>) => {
  return Api.post(API_TEMPORARY_AUTH_SIGNIN, data)
}

export const resetTemporaryUserPassword = (data: Record<string, any>) => {
  return Api.post(API_TEMPORARY_AUTH_RESET_PWD, data)
}

export const sendTemporaryForgetPasswordEmail = (data: Record<string, any>) => {
  return Api.post(API_TEMPORARY_AUTH_FORGOT_PWD, data)
}

export const checkTemporaryUserExists = (email: string) => {
  return Api.post(API_TEMPORARY_AUTH_EXISTS_EMAIL, { email })
}

export const registerTemporaryUser = (data: Record<string, any>) => {
  return Api.post(API_TEMPORARY_AUTH_REGISTER, data)
}

// ---- Borrower (temporary user) 2FA: OTP + passkey --------------------------

export const sendTemporary2faCode = (data: Record<string, string>) => {
  return Api.post(API_TEMPORARY_AUTH_SEND_2FA_CODE, data)
}

export const verifyTemporary2faCode = (data: Record<string, string>) => {
  return Api.post(API_TEMPORARY_AUTH_VERIFY_2FA_CODE, data)
}

// Enrollment (requires a signed-in borrower session).
export const getTemporaryPasskeyRegisterOptions = () => {
  return Api.post(API_TEMPORARY_PASSKEY_REGISTER_OPTIONS, {})
}

export const verifyTemporaryPasskeyRegistration = (data: { response: any; name?: string }) => {
  return Api.post(API_TEMPORARY_PASSKEY_REGISTER_VERIFY, data)
}

export const listTemporaryPasskeys = () => {
  return Api.get(API_TEMPORARY_PASSKEY_LIST)
}

export const deleteTemporaryPasskey = (id: number) => {
  return Api.delete(API_TEMPORARY_PASSKEY_DELETE, {}, { id })
}

// Login second factor.
export const getTemporaryPasskeyLoginOptions = (email: string) => {
  return Api.post(API_TEMPORARY_PASSKEY_LOGIN_OPTIONS, { email })
}

export const verifyTemporaryPasskeyLogin = (data: { email: string; password: string; response: any }) => {
  return Api.post(API_TEMPORARY_PASSKEY_LOGIN_VERIFY, data)
}

export const signInInvestorUser = (data: Record<string, any>) => {
  return Api.post(API_INVESTOR_AUTH_SIGNIN, data)
}

export const resetInvestorUserPassword = (data: Record<string, any>) => {
  return Api.post(API_INVESTOR_AUTH_RESET_PWD, data)
}

export const sendInvestorForgetPasswordEmail = (data: Record<string, any>) => {
  return Api.post(API_INVESTOR_AUTH_FORGOT_PWD, data)
}

export const signInParticipantUser = (data: Record<string, any>) => {
  return Api.post(API_PARTICIPANT_AUTH_SIGNIN, data)
}

export const resetParticipantUserPassword = (data: Record<string, any>) => {
  return Api.post(API_PARTICIPANT_AUTH_RESET_PWD, data)
}

export const sendParticipantForgetPasswordEmail = (data: Record<string, any>) => {
  return Api.post(API_PARTICIPANT_AUTH_FORGOT_PWD, data)
}
